import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { View, TouchableWithoutFeedback } from 'react-native';
import { interpolateColor, useAnimatedScrollHandler, useAnimatedStyle, useSharedValue, } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import FakeSearchBar from 'design-system/FakeSearchBar';
import { SEARCH_BAR_HEIGHT } from 'design-system/SearchBar';
import IconFilterBold from 'design-system/icons/IconFilterBold';
import { AnimatedTouchableBounce } from 'design-system/AnimatedComponents';
import MultiSelectAccountModal from 'design-system/MultiSelectAccountModal';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import { selectTransactionFilterAccounts } from 'reducers/selectors';
import createStyleSheets from 'utils/createStyleSheets';
import { useDefaultHeaderHeight } from 'hooks/useHeaderHeight';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { isWeb } from '../../../constants';
import { isAllVisibleAccountsSelected, normalizeSelectedAccounts, } from '../utils/accountFilter';
import { useOpenSearch } from '../hooks/useOpenSearch';
const checkEquality = (obj1, obj2) => {
    const obj1Keys = Object.keys(obj1);
    const obj2Keys = Object.keys(obj2);
    if (obj1Keys.length === obj2Keys.length) {
        let areTheSame = true;
        obj1Keys.every((key) => {
            const hasKeyInObj2 = !!obj2[Number(key)];
            if (!hasKeyInObj2) {
                areTheSame = false;
                return false;
            }
            return true;
        });
        return areTheSame;
    }
    return false;
};
const TxnScreenNavHeaderExtension = ({ scrollY, filters, setFilters, showHiddenSection = false }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const accounts = useAppSelector(selectTransactionFilterAccounts);
    const accountsWithoutHidden = useMemo(() => {
        const filtered = accounts.filter((acc) => !acc.isHidden);
        return filtered;
    }, [accounts]);
    const openSearch = useOpenSearch();
    const { top } = useSafeAreaInsets();
    const headerHeight = useDefaultHeaderHeight();
    const selectedAccountsRef = useRef([]);
    useEffect(() => {
        // if no accounts selected in filters, select all accounts
        if (Object.keys(filters.selectedAccountsObj).length === 0) {
            selectedAccountsRef.current = accountsWithoutHidden.map((acc) => acc.id);
        }
        else {
            // Otherwise use the selected accounts from filters
            selectedAccountsRef.current = Object.keys(filters.selectedAccountsObj).map(Number);
        }
    }, [filters.selectedAccountsObj, accountsWithoutHidden]);
    const handleSave = useCallback((selectedAccounts, showExcludedTxns) => {
        const newSelectedAccObj = normalizeSelectedAccounts(selectedAccounts.reduce((a, b) => ({
            ...a,
            [b]: true,
        }), {}), accounts);
        if (filters.showExcludedTxns !== showExcludedTxns ||
            !checkEquality(filters.selectedAccountsObj, newSelectedAccObj)) {
            selectedAccountsRef.current = selectedAccounts;
            setFilters(newSelectedAccObj, !!showExcludedTxns);
        }
    }, [
        accounts,
        filters.selectedAccountsObj,
        filters.showExcludedTxns,
        setFilters,
    ]);
    const scrollValue = useSharedValue(0);
    const onScroll = useAnimatedScrollHandler((handle) => {
        scrollValue.value = handle.contentOffset.y;
    }, []);
    const handleFilterPress = useCallback(() => {
        MultiSelectAccountModal.show({
            accounts,
            showSelectAll: true,
            onSelected: handleSave,
            alreadySelected: selectedAccountsRef.current,
            showExcludedTxns: filters.showExcludedTxns,
            onScroll,
            scrollValue,
            showHiddenSection,
        });
    }, [
        accounts,
        filters.showExcludedTxns,
        handleSave,
        onScroll,
        scrollValue,
        showHiddenSection,
    ]);
    const handleSearchPress = useCallback(() => {
        openSearch(isWeb
            ? {
                selectedAccountsObj: filters.selectedAccountsObj,
            }
            : {
                searchBarY: headerHeight - top,
                selectedAccountsObj: filters.selectedAccountsObj,
            });
    }, [filters.selectedAccountsObj, headerHeight, openSearch, top]);
    const bgColorStyle = useAnimatedStyle(() => ({
        backgroundColor: interpolateColor(scrollY.value, [0, 40], [
            colors.cards.onDark,
            colors.background.dark,
        ]),
    }), [colors.background.dark, colors.cards.onDark, scrollY]);
    const isAllSelectedButHidden = useMemo(() => isAllVisibleAccountsSelected(filters.selectedAccountsObj, accounts), [accounts, filters.selectedAccountsObj]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    return (<View style={[styles.container, paddingHorizontalStyle]}>
        <TouchableWithoutFeedback onPress={handleSearchPress} style={styles.searchBarBtn}>
          <View pointerEvents="box-only" style={styles.flex}>
            <FakeSearchBar scrollY={scrollY} style={styles.fakeSearchBar}/>
          </View>
        </TouchableWithoutFeedback>
        {(!isWeb || !!accounts?.length) && (<>
            <Spacer w={8}/>
            <AnimatedTouchableBounce onPress={handleFilterPress} hoverStyle={styles.hoverOnDark} style={[styles.filter, bgColorStyle]}>
              <IconFilterBold color={colors.text.secondary} dot={!filters.showExcludedTxns || !isAllSelectedButHidden}/>
            </AnimatedTouchableBounce>
          </>)}
      </View>);
};
export default memo(TxnScreenNavHeaderExtension);
const styleSet = createStyleSheets((colors) => ({
    searchBarBtn: {
        flex: 1,
        ...(isWeb && {
            cursor: 'pointer',
        }),
    },
    container: {
        flexDirection: 'row',
        paddingBottom: rem(8),
    },
    flex: {
        flex: 1,
    },
    fakeSearchBar: {
        paddingRight: 0,
        marginBottom: 0,
    },
    filter: {
        aspectRatio: 1,
        marginTop: rem(8),
        marginRight: rem(16),
        alignItems: 'center',
        borderRadius: rem(12),
        justifyContent: 'center',
        height: SEARCH_BAR_HEIGHT,
    },
    hoverOnDark: {
        backgroundColor: colors.control.hoverOnDark,
    },
}));
